//frontend/src/components/users/ForgotPassword.js
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { forgotPassword, verifyOTP, resetPassword, clearAuthError } from '../../actions/userActions';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import MetaData from '../layouts/MetaData';
import { Mail, Lock, KeyRound, ChevronRight } from 'lucide-react';

export default function ForgotPassword() {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { error, message, loading } = useSelector(state => state.authState);

    useEffect(() => {
        if (error) {
            toast.error(error);
            dispatch(clearAuthError());
            setIsSubmitting(false);
        }
        if (message) {
            if (step === 1) {
                toast.success('OTP sent successfully');
                setStep(2);
            }
            setIsSubmitting(false);
        }
    }, [dispatch, error, message, step]);

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        if (!email) {
            toast.error('Please enter email');
            return;
        }
        setIsSubmitting(true);
        const formData = new FormData();
        formData.append('email', email);
        await dispatch(forgotPassword(formData));
    };

    const handleOTPSubmit = async (e) => {
        e.preventDefault();
        if (!otp) {
            toast.error('Please enter OTP');
            setIsSubmitting(false);
            return;
        }
        setIsSubmitting(true);
        try {
            await dispatch(verifyOTP(email, otp));
            setStep(3);
            setIsSubmitting(false);
        } catch (error) {
            setIsSubmitting(false);
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        setIsSubmitting(true);
        try {
            await dispatch(resetPassword({
                email,
                otp,
                newPassword,
                confirmPassword
            }));
            toast.success('Password reset successfully');
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (error) {
            setIsSubmitting(false);
        }
    };

    const renderStepIndicator = () => (
        <div className="flex items-center justify-center mb-4">
            {[1, 2, 3].map((index) => (
                <div key={index} className="flex items-center">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                        step >= index ? 'bg-[#dc2626] text-white' : 'bg-gray-200 text-gray-500'
                    }`}>
                        {index}
                    </div>
                    {index < 3 && (
                        <div className={`w-8 h-1 ${
                            step > index ? 'bg-[#dc2626]' : 'bg-gray-200'
                        }`} />
                    )}
                </div>
            ))}
        </div>
    );

    const renderEmailForm = () => (
        <form className="space-y-4" onSubmit={handleEmailSubmit}>
            <div>
                <label htmlFor="email_field" className="block text-xs font-medium text-gray-700">
                    Email Address
                </label>
                <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                        type="email"
                        id="email_field"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center text-sm h-10"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                    />
                </div>
            </div>
            <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-xs font-medium text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] h-10 items-center"
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Sending...' : 'Send OTP'}
                <ChevronRight className="ml-2 h-4 w-4" />
            </button>
        </form>
    );

    const renderOTPForm = () => (
        <form className="space-y-4" onSubmit={handleOTPSubmit}>
            <div>
                <label htmlFor="otp_field" className="block text-xs font-medium text-gray-700">
                    OTP Verification
                </label>
                <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <KeyRound className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        id="otp_field"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center text-sm h-10"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                        placeholder="Enter OTP"
                        required
                    />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                    Please enter the OTP sent to {email}
                </p>
            </div>
            <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-xs font-medium text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] h-10 items-center"
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Verifying...' : 'Verify OTP'}
                <ChevronRight className="ml-2 h-4 w-4" />
            </button>
        </form>
    );

    const renderPasswordForm = () => (
        <form className="space-y-4" onSubmit={handlePasswordReset}>
            <div>
                <label htmlFor="new_password_field" className="block text-xs font-medium text-gray-700">
                    New Password
                </label>
                <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                        type="password"
                        id="new_password_field"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center text-sm h-10"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        required
                    />
                </div>
            </div>
            <div>
                <label htmlFor="confirm_password_field" className="block text-xs font-medium text-gray-700">
                    Confirm Password
                </label>
                <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                        type="password"
                        id="confirm_password_field"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] text-center text-sm h-10"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm new password"
                        required
                    />
                </div>
            </div>
            <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-xs font-medium text-white bg-[#dc2626] hover:bg-[#dc2626]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#dc2626] h-10 items-center"
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
                <ChevronRight className="ml-2 h-4 w-4" />
            </button>
        </form>
    );

    return (
        <>
            <MetaData title="Forgot Password" />
            <div 
                className="min-h-screen flex flex-col justify-center py-4"
                style={{
                    backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-background-image.png)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="sm:mx-auto sm:w-full sm:max-w-md">
                    <h2 className="text-center text-xl font-bold text-[#dc2626] font-['Crimson_Text']">
                        Reset Password
                    </h2>
                    <p className="mt-1 text-center text-xs text-gray-600">
                        {step === 1 && "Enter your email to receive an OTP"}
                        {step === 2 && "Enter the OTP sent to your email"}
                        {step === 3 && "Create a new password"}
                    </p>
                </div>

                <div className="mt-2 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="bg-white py-4 px-4 shadow sm:rounded-lg sm:px-6">
                        {renderStepIndicator()}
                        {step === 1 && renderEmailForm()}
                        {step === 2 && renderOTPForm()}
                        {step === 3 && renderPasswordForm()}
                    </div>
                </div>
            </div>
        </>
    );
}